import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsEvent, useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import { Input, Toast, Typography, useInput } from '@chilecompra/react-kit/components'
import { formatDNI } from '@chilecompra/react-kit/utils'
import {
  isLocalEnv,
  REACT_APP_FEATURE_FLAG_RECOVERY_ADMIN,
  REACT_APP_FEATURE_FLAG_TAXES_KEY_ENABLED,
  REACT_APP_SII_TAXES_URL,
  REACT_APP_TAXES_KEY_SEED,
  REACT_APP_UNIQUE_PASS_URL
} from '../../../config/settings/environment'
import { useCsrfToken } from '../../../modules/hooks/security'
import { checkIsNaturalPerson } from '../../../modules/utils/helpers'
import { onSignInTaxesKeyThunk } from '../../TaxesKeyAuth/TaxesKeyAuth.actions'
import { onCheckGuestBusinessThunk, onResetRegisterWizardState, registerFlowThunk } from '../RegisterWizard.actions'
import ProtectByFeatures from '../../../components/Security/ProtectByFeatures'
import {
  Root,
  Section,
  Strong,
  Title,
  Instructions,
  Form,
  Search,
  SearchClearIcon,
  SearchIcon,
  SearchLoadingIcon,
  TaxesKeyInstructions,
  TaxesKey,
  TaxesKeyHelp,
  TaxesKeyHelpLink,
  Image,
  ToastContainer,
  Hidden,
  Divider,
  ToastFontContainer
} from './RegisterWizardBusinessSearch.styles'
import { Button, Spinner, useEffectOnce, useViewport } from '@chilecompra/react-kit'
import { Link } from '../Step0/RegisterWizardWelcome.styles'

/**
 * The RegisterWizardBusinessSearch's container.
 */
const RegisterWizardBusinessSearch = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const [clear, setClear] = useState(false)
  const { size } = useViewport()
  const { business, errors, loading } = useSelector(state => state.registerWizard)
  const {
    value: dniValue,
    onChange: handleChange,
    setValue: setDNIValue
  } = useInput({
    initialValue: business.dni || '',
    changeCallback: () => setClear(false),
    formatCallbacks: [formatDNI],
    overwriteCallback: () => business.dni
  })
  const flow = 'INICIO_PASO_3'

  const csrfToken = useCsrfToken(REACT_APP_TAXES_KEY_SEED)
  const makeSubmission = () => {
    setClear(true)
    dispatch(
      onCheckGuestBusinessThunk({
        dni: dniValue,
        onSuccessCallback: () => onChangeStep('business-info')
      })
    )
  }

  const [isNaturalPerson, setIsNaturalPerson] = useState(true)
  const handleClick = () => {
    setIsNaturalPerson(checkIsNaturalPerson(dniValue))
    if (!clear) {
      return makeSubmission()
    }

    setClear(false)
    setDNIValue('')
  }
  const handleBack = () => {
    dispatch(onResetRegisterWizardState())
    onChangeStep('welcome')
  }
  const handleSubmit = event => {
    event.preventDefault()
    makeSubmission()
  }
  const handleTaxesKey = () => {
    if (
      isLocalEnv ||
      REACT_APP_FEATURE_FLAG_TAXES_KEY_ENABLED === false ||
      REACT_APP_FEATURE_FLAG_TAXES_KEY_ENABLED === 'false'
    ) {
      onChangeStep('business-info')
    } else {
      dispatch(onSignInTaxesKeyThunk({ csrfToken, dni: dniValue }))
    }
  }

  const handleRecoverAdmin = () => {
    if ([true, 'true'].includes(REACT_APP_FEATURE_FLAG_RECOVERY_ADMIN)) {
      onChangeStep('recovery-company')
    } else {
      window.open(REACT_APP_UNIQUE_PASS_URL, '_blank')
    }
  }

  useAnalyticsEvent({ action: 'Finaliza registro de usuario', category: 'registrar-usuario' })
  useAnalyticsPageView('/paso/3/buscar_empresa')

  useEffectOnce(() => {
    dispatch(registerFlowThunk({ flow }))
  }, [])

  const renderToast = () => {
    return (
      <ToastContainer role="alert" aria-live="assertive">
        <Toast
          tabIndex="0"
          disableIcon
          severity="default"
          width={size.isTiny ? '90%' : size.isDownToLarge ? '100%' : '263px'}
        >
          Recuerda que se te <Strong>asignará el rol de administrador</Strong> de esta institución en Mercado Público.
        </Toast>
      </ToastContainer>
    )
  }

  return (
    <Root>
      <Section display="flex" flexDirection={size.isTiny ? 'column' : 'row'} justifyContent="space-between" gap="50px">
        <Section>
          <Section item>
            <Title tabIndex="0" aria-label="Paso 3: Inscribir una nueva institución en Mercado Público">
              Inscribir una nueva institución en Mercado Público
            </Title>
          </Section>
          <Section item margin={size.isTiny ? '10px 0' : '24px 0 32px'} maxWidth="521px">
            <Instructions tabIndex="0">
              Ingresa el RUT de la institución que deseas registrar como comprador en la plataforma.
            </Instructions>
            {size.isTiny && renderToast()}
          </Section>
          <Section item display="flex" margin={size.isTiny && '120px 0'}>
            <Form onSubmit={handleSubmit}>
              <Input
                label="RUT Institución"
                aria-label="RUT Institución"
                suffix={
                  <Search
                    clear={clear && !loading}
                    disabled={dniValue === '' || loading}
                    type="button"
                    aria-label="Buscar RUT"
                    onClick={handleClick}
                  >
                    {!clear && !loading && <SearchIcon />}
                    {loading && <SearchLoadingIcon />}
                    {clear && !loading && <SearchClearIcon />}
                  </Search>
                }
                value={dniValue}
                onChange={handleChange}
                autoFocus
              />
              {size.isDownToLarge && !size.isTiny && renderToast()}
            </Form>
          </Section>

          {loading && (
            <Section
              role="status"
              aria-label="Cargando..."
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginTop={size.isTiny ? '30px' : size.isDownToLarge ? '20px' : '40px'}
            >
              <Spinner />
            </Section>
          )}

          <Section item margin={errors.businessSearchError ? '0 0 22px' : 'initial'}>
            {errors.businessSearchError === 'SOMETHING_WENT_WRONG_ERROR' && !loading && (
              <ToastFontContainer>
                <Toast tabIndex="0" severity="error">
                  Lo sentimos, estamos presentando problemas para traer la información asociada a los RUT. Por favor
                  intenta de nuevo en unos minutos.
                </Toast>
              </ToastFontContainer>
            )}
            {errors.businessSearchError === 'DNI_BELONGS_TO_A_DECEASED_ERROR' && !loading && (
              <ToastFontContainer>
                <Toast severity="error">
                  Lo sentimos, este rut no se encuentra vigente. Consulta el detalle directamente en el Registro Civil.
                </Toast>
              </ToastFontContainer>
            )}
            {errors.businessSearchError === 'DNI_NOT_ALLOWED_ERROR' && !loading && (
              <ToastFontContainer>
                <Toast tabIndex="0" severity="warning">
                  El RUT ingresado corresponde a persona natural.
                </Toast>
              </ToastFontContainer>
            )}
            {errors.businessSearchError === 'DNI_DOES_NOT_MATCH_ERROR' && !loading && (
              <ToastFontContainer>
                <Toast tabIndex="0" severity="error">
                  Hemos detectado que ingresaste un RUT de otra persona, verifica que lo estés ingresando correctamente
                  e intenta de nuevo.
                </Toast>
              </ToastFontContainer>
            )}
            {errors.businessSearchError === 'DNI_ALREADY_REGISTERED' && !loading && (
              <>
                <ToastFontContainer>
                  <Toast tabIndex="0" severity="error" maxWidth={size.isTiny ? '325px' : '450px'} margin="0 0 15px 0">
                    La institución ya está registrada en Mercado Público.
                  </Toast>
                </ToastFontContainer>
                <ProtectByFeatures rule={features => features['com.buyer/recovery.admin']}>
                  <Section item>
                    {!isNaturalPerson && (
                      <Typography tabIndex="0" margin="20px 0" textAlign="left" fontSize="14px" lineHeight="18px">
                        Si la o las personas que contaban con el rol de administrador ya no se encuentran disponibles,
                        puedes{' '}
                        <Link
                          tabIndex="0"
                          aria-label="Solicitar administrar esta institución"
                          color="primary"
                          onClick={handleRecoverAdmin}
                          rel="noopener noreferrer"
                          underline="true"
                        >
                          solicitar administrar esta institución a través del siguiente formulario.
                        </Link>{' '}
                        Deberás contar con la Clave Tributaria de la institución.
                      </Typography>
                    )}

                    {isNaturalPerson && (
                      <Typography tabIndex="0" fontSize="13px" lineHeight="18px">
                        Si requieres administrar esta empresa,{' '}
                        <Strong> solicita al usuario con rol Administrador </Strong>
                        que lo realice en la plataforma ingresando a la pestaña Usuarios, desde el menú Mi Empresa.
                      </Typography>
                    )}
                  </Section>
                </ProtectByFeatures>
              </>
            )}
            <ProtectByFeatures rule={features => features['com.buyer/activities']}>
              {errors.businessSearchError === 'DNI_NOT_INITIALIZED_ERROR' && !loading && (
                <ToastFontContainer>
                  <Toast tabIndex="0" severity="warning">
                    La institución <Strong>{errors?.orgLegalName}</Strong> no registra inicio de actividades en Servicio
                    de Impuestos Internos.
                  </Toast>
                </ToastFontContainer>
              )}
            </ProtectByFeatures>
            {errors.businessSearchError === 'PENDING_REQUEST' && !loading && (
              <ToastFontContainer>
                <Toast tabIndex="0" severity="info">
                  La institución <Strong>{errors?.orgLegalName}</Strong> actualmente ya cuenta con la solicitud de
                  inscripción en estado pendiente de revisión.
                  <Divider />
                  Si deseas conocer el estado de esta solicitud puedes contactar a la Mesa de Ayuda de ChileCompra al
                  teléfono 600 7000 600.
                </Toast>
              </ToastFontContainer>
            )}{' '}
            {errors.businessSearchError === 'DNI_DOES_NOT_MATCH_TAXES_KEY_ERROR' && !loading && (
              <ToastFontContainer>
                <Toast tabIndex="0" severity="warning">
                  El RUT ingresado de la institución no coincide con el RUT autentificado con Clave Tributaria.
                  <Divider />
                  Revisa los datos ingresados en el paso anterior.
                </Toast>
              </ToastFontContainer>
            )}
            {['', undefined, null].includes(errors.businessSearchError) &&
              business?.entityType === 'legal' &&
              !loading && (
                <>
                  <Section item>
                    <TaxesKeyInstructions tabIndex="0">
                      Hemos detectado que este RUT corresponde a la empresa <Strong>{business?.name}</Strong>.
                      <br />
                      <br />
                      Para validar que cuentas con la autorización para realizar la inscripción del organismo comprador
                      deberás ingresar con la Clave Tributaria de la institución en el siguiente botón
                    </TaxesKeyInstructions>
                  </Section>
                  <Section>
                    <TaxesKey>
                      <Button
                        tabIndex="0"
                        alt="Clave Tributaria"
                        onClick={handleTaxesKey}
                        aria-label="Ingresar clave tributaria"
                      >
                        Ingresar clave tributaria
                      </Button>
                      <TaxesKeyHelp>
                        <TaxesKeyHelpLink
                          tabIndex="0"
                          aria-label="¿Qué es Clave Tributaria?"
                          href={REACT_APP_SII_TAXES_URL}
                          target="_blank"
                        >
                          ¿Qué es Clave Tributaria?
                        </TaxesKeyHelpLink>
                      </TaxesKeyHelp>
                    </TaxesKey>
                  </Section>
                </>
              )}
          </Section>
        </Section>
        <Section display="flex" flexDirection="column">
          <Hidden lgDown>
            <Image src="/assets/img/steps/paso4.svg" alt="Ilustración del paso 4: Inscripción de nueva institución" />
          </Hidden>
          {size.isUpToLarge && renderToast()}
        </Section>
      </Section>

      <Button
        tabIndex="0"
        aria-label="Cancelar"
        onClick={handleBack}
        type="button"
        width={size.isTiny ? '95%' : '156px'}
        variant="outlined"
        margin={size.isTiny ? '50px 0' : size.isDownToLarge ? '27px 0' : '30px 0'}
      >
        Cancelar
      </Button>
    </Root>
  )
}

RegisterWizardBusinessSearch.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardBusinessSearch.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardBusinessSearch
